<template>
  <div
    class="mega-menu"
    :class="`${themeClass}`"
    :style="`
      --mega-menu-bg-mobile: ${megaMenuTheme.mobile.background};
      --mega-menu-bg-desktop: ${megaMenuTheme.desktop.background};`
    "
    @mouseleave="handleLeave"
  >
    <div class="mega-menu__grid grid">
      <div
        class="mega-menu__column mega-menu__column--left col l6"
      >
        <!-- Nav content -->
        <div class="mega-menu__navigation grid">
          <div class="mega-menu__tier-one col l6">
            <clickable-list
              ref="tierOne"
              activate-on-hover
              block-class="mega-menu"
              :links="clickableListContent"
              :selected-item="tierOne"
              @link-clicked="tierOneClicked"
            />
          </div>

          <div
            class="mega-menu__tier-two col l6"
            @mouseleave="handleTierOneLeave"
          >
            <clickable-list
              v-if="tierTwoContent"
              block-class="mega-menu"
              :links="tierTwoContent"
            />
          </div>
        </div>

        <!-- CTA - hides if no label is set. -->
        <btn
          v-if="megaNavCta.label"
          class="mega-menu__cta-button"
          :label="megaNavCta.label"
          :modifiers="megaNavCta.style"
          :show-text-icon="false"
          :url="megaNavCta.url"
        />
      </div>
      <div class="mega-menu__column mega-menu__column--right col l6">
        <menu-carousel
          :autoplay-speed="carouselInterval"
          namespace="mega-menu"
          :slides="slides"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Mega menu (mega-menu)
 * -----------------------------------------------------------------------------
 * Desktop mega menu component.
 *
 * @param {Number} carouselInterval - Number of secs between slides on carousel.
 * @param {Object} desktop - Desktop linklist handle and items.
 * @param {Object} megaMenuTheme - Theme settings for the mega menu.
 * @param {Object} megaNavCta - Mega Nav CTA settings.
 * @param {Array} [slides] - Array of slide objects created from section blocks.
 *
 */
import { formatLinklist } from '~/helpers/format'

import Btn from '~global/btn/btn'
import ClickableList from '~global/clickable-list/clickable-list'
import MenuCarousel from '~async/menu-carousel/menu-carousel'

import { mapState } from 'vuex'

export default {
  name: 'MegaMenu',

  components: {
    Btn,
    ClickableList,
    MenuCarousel,
  },

  props: {
    carouselInterval: {
      type: Number,
      default: 2500,
    },

    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },

    megaMenuTheme: {
      type: Object,
      default: () => ({
        desktop: {
          background: '',
          content: '',
        },
        mobile: {
          background: '',
          content: '',
        },
      }),
    },

    megaNavCta: {
      type: Object,
      default: () => ({
        label: '',
        url: '',
        style: 'primary',
      }),
    },

    slides: {
      type: Array,
      default: () => [],
    },
  },

  emits:
    {
      'mega-menu-close': (payload) => typeof payload === 'undefined',
    },

  data() {
    return {
      cnvsGlobal: true,
      tierOne: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns and interpolated theme class to use for theming.
     * @returns {String}
     */
    themeClass() {
      return this.isMobile
        ? `section-content--${this.megaMenuTheme.mobile.content}`
        : `section-content--${this.megaMenuTheme.desktop.content}`
    },

    /**
     * Returns a formatted list of linklist items for use in clickable-list
     * component.
     * @returns {Array}
     */
    clickableListContent() {
      return this.desktop.items.map(linklist => formatLinklist(linklist))
    },

    /**
     * Returns whether tier2 content exists.
     * @returns {Array | Boolean}
     */
    tierTwoContent() {
      return this.tierOne?.links?.length
        ? this.tierOne.links.map(linklist => formatLinklist(linklist))
        : false
    },
  },

  methods: {

    /**
     * Handles the mouseleave event.
     */
    handleTierOneLeave() {
      this.tierOne = null
      this.$refs.tierOne.resetComponentData()
    },

    /**
     * Handles the mouseleave event.
     */
    handleLeave() {
      this.$emit('mega-menu-close')
    },

    /**
     * Handles click event on tier1 menu item.
     * @param {Object} value - Object of tier1 link data.
     */
    tierOneClicked(value) {
      this.tierOne = value
    },
  },
}
</script>
